// TUT-450 — as solicitações de serviço: lista, criação, aceite e recusa.
import { demoApi } from "../demo-data";
import type {
  AcceptServiceRequestInput,
  AcceptServiceRequestResult,
  AgendamentoDiretoInput,
  AgendarInput,
  ExpertReport,
  FichaDeAgendamentoResponse,
  CreateServiceRequestInput,
  RefuseServiceRequestInput,
  ServiceRequestListQuery,
  ServiceRequestListResponse,
  ServiceRequestRecord,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDasSolicitacoes = {
  // TUT-291a/TUT-292 — a solicitação como dado novo: criar, listar, aceitar
  // (que CRIA o trabalho por trás) e recusar. Ver `lib/types.ts` para o
  // espelho dos DTOs da API.
  serviceRequests(
    query: ServiceRequestListQuery = {},
  ): Promise<ServiceRequestListResponse> {
    if (useDemoApi) return demoApi.serviceRequests(query);
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      pageSize: String(query.pageSize ?? 25),
    });
    if (query.status) params.set("status", query.status);
    if (query.kind) params.set("kind", query.kind);
    if (query.agendamento) params.set("agendamento", query.agendamento);
    return request<ServiceRequestListResponse>(`/service-requests?${params}`);
  },
  createServiceRequest(
    input: CreateServiceRequestInput,
  ): Promise<ServiceRequestRecord> {
    if (useDemoApi) return demoApi.createServiceRequest(input);
    return request<ServiceRequestRecord>("/service-requests", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  acceptServiceRequest(
    id: string,
    input: AcceptServiceRequestInput = {},
  ): Promise<AcceptServiceRequestResult> {
    if (useDemoApi) return demoApi.acceptServiceRequest(id, input);
    return request<AcceptServiceRequestResult>(
      `/service-requests/${encodeURIComponent(id)}/accept`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  /** TUT-526 — a ficha de agendamento como abre: preenchida da solicitação. */
  fichaDeAgendamento(id: string): Promise<FichaDeAgendamentoResponse> {
    if (useDemoApi) return demoApi.fichaDeAgendamento(id);
    return request<FichaDeAgendamentoResponse>(
      `/service-requests/${encodeURIComponent(id)}/ficha-de-agendamento`,
    );
  },
  /** TUT-526 — grava a ficha: cria a peça ou o laudo, ou agenda a perícia. */
  agendarSolicitacao(id: string, input: AgendarInput): Promise<AcceptServiceRequestResult> {
    if (useDemoApi) return demoApi.agendarSolicitacao(id, input);
    return request<AcceptServiceRequestResult>(
      `/service-requests/${encodeURIComponent(id)}/agendar`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  /** TUT-568 — o "Novo agendamento": a solicitação já aceita e agendada num passo. */
  agendamentoDireto(input: AgendamentoDiretoInput): Promise<AcceptServiceRequestResult> {
    if (useDemoApi) return demoApi.agendamentoDireto(input);
    return request<AcceptServiceRequestResult>("/service-requests/agendamento-direto", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** TUT-526 (D39) — a ficha do laudo pendente de agendamento. */
  fichaDoLaudo(reportId: string): Promise<FichaDeAgendamentoResponse> {
    if (useDemoApi) return demoApi.fichaDoLaudo(reportId);
    return request<FichaDeAgendamentoResponse>(
      `/expert-reports/${encodeURIComponent(reportId)}/ficha-de-agendamento`,
    );
  },
  /** TUT-526 (D39) — agenda o laudo pendente: ele vai para "Aguardando elaboração". */
  agendarLaudo(reportId: string, input: AgendarInput): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.agendarLaudo(reportId, input);
    return request<ExpertReport>(`/expert-reports/${encodeURIComponent(reportId)}/agendar`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  /** TUT-525 (D22) — o motivo é obrigatório: sem ele, a API responde 400. */
  refuseServiceRequest(
    id: string,
    input: RefuseServiceRequestInput,
  ): Promise<ServiceRequestRecord> {
    if (useDemoApi) return demoApi.refuseServiceRequest(id, input);
    return request<ServiceRequestRecord>(
      `/service-requests/${encodeURIComponent(id)}/refuse`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
};
